import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Button as MuiButton, Stack, Typography } from '@mui/material';
import type { MouseEventHandler, ReactNode } from 'react';
import * as UI from '@siloreal:shared:ui';
import { useSilobagListContext } from '../../context/silobag-list-context';
import { HeaderActionsDropdown } from './header-actions-dropdown';
import * as HOOKS from '@siloreal:shared:hooks';

const TAB_BUTTON_BLUE = '#22A7F0';
const TAB_BUTTON_ACTIVE_BACKGROUND = '#EAF8FF';
const TAB_BUTTON_IDLE_BORDER = '#DFE3EA';

export function SilosProHeader() {
  const {
    state: { current, change },
  } = useSilobagListContext();

  const activeTab = current.view === 'silospro' ? current.tab : null;

  const i18n = HOOKS.useLocale();

  return (
    <UI.Card siloreal>
      <Stack direction="row" alignItems="center" gap={4} flexWrap="wrap" px={3} py={2} sx={{ minHeight: 84 }}>
        <Stack alignItems="flex-start" gap={0.25} sx={{ minWidth: 170 }}>
          <Typography fontSize={24} fontWeight={900} color="#32422B" lineHeight={1.05}>
            {i18n.t('temp.private.list.silospro.silospro')}
          </Typography>
          <Typography fontSize={13} color="#2F332E">
            {i18n.t('temp.private.list.silospro.ready_to_operate')}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" gap={1.5}>
          <SilosProTabButton
            active={activeTab === 'vrr'}
            subtitle={i18n.t('temp.private.list.silospro.silospro')}
            title={i18n.t('temp.private.list.silospro.vrr')}
            icon={<VerifiedIcon sx={{ color: '#42db37', fontSize: 24 }} />}
            iconBackgroundColor="#E6F8E4"
            onClick={() => change.tab.silospro('vrr')}
            tid="silobag-list-tab-vrr"
          />
          <SilosProTabButton
            active={activeTab === 'no-vrr'}
            subtitle={i18n.t('temp.private.list.silospro.silospro')}
            title={i18n.t('temp.private.list.silospro.without_vrr')}
            icon={<VerifiedIcon sx={{ color: '#CFCFD4', fontSize: 24 }} />}
            onClick={() => change.tab.silospro('no-vrr')}
            tid="silobag-list-tab-without-vrr"
          />
          <SilosProTabButton
            active={activeTab === 'closed'}
            subtitle={i18n.t('temp.private.list.silospro.silospro')}
            title={i18n.t('temp.common.closed')}
            icon={<LockIcon sx={{ color: '#49454F', fontSize: 24 }} />}
            onClick={() => change.tab.silospro('closed')}
            tid="silobag-list-tab-closed"
          />
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.25} sx={{ marginLeft: 'auto' }}>
          <HeaderActionsDropdown
            view="silospro"
            tid="silobag-list-actions-btn"
            popupTid="silobag-list-actions-popup"
          />
          <UI.Button
            variant="primary"
            icon={{ value: <ArrowForwardRoundedIcon fontSize="small" />, position: 'end' }}
            onClick={() => change.view.silobag()}
            tid="silobag-list-silobolsas-btn"
          >
            {i18n.t('temp.common.silobag')}
          </UI.Button>
        </Stack>
      </Stack>
    </UI.Card>
  );
}

type ActiveProp = { active?: boolean };
type DisplayProp = { title: string; subtitle: string; icon: ReactNode };
type ActionProp = { onClick?: MouseEventHandler<HTMLButtonElement> };
type ExtraProp = { tid?: string; ariaLabel?: string; iconBackgroundColor?: string };
type SilosProTabButtonProps = ActiveProp & DisplayProp & ActionProp & ExtraProp;

function SilosProTabButton(props: SilosProTabButtonProps) {
  const { active = false } = props;
  const { title, subtitle, icon } = props;
  const { onClick } = props;
  const { tid, ariaLabel, iconBackgroundColor = '#F1F1F4' } = props;
  return (
    <MuiButton
      type="button"
      variant="outlined"
      onClick={onClick}
      data-testid={tid}
      aria-label={ariaLabel}
      disableElevation
      sx={{
        minWidth: 188,
        height: 68,
        justifyContent: 'flex-start',
        px: 2,
        py: 1.25,
        borderRadius: '10px',
        borderColor: active ? TAB_BUTTON_BLUE : TAB_BUTTON_IDLE_BORDER,
        borderWidth: active ? 2 : 1,
        backgroundColor: active ? TAB_BUTTON_ACTIVE_BACKGROUND : '#FFFFFF',
        color: '#3A3440',
        textTransform: 'none',
        boxShadow: active ? '0 8px 20px rgba(34, 167, 240, 0.10)' : 'none',
        '&:hover': {
          borderColor: active ? TAB_BUTTON_BLUE : TAB_BUTTON_IDLE_BORDER,
          borderWidth: active ? 2 : 1,
          backgroundColor: active ? TAB_BUTTON_ACTIVE_BACKGROUND : '#FFFFFF',
        },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: iconBackgroundColor,
            lineHeight: 0,
          }}
        >
          {icon}
        </Box>

        <Stack alignItems="flex-start" lineHeight={1.1}>
          <Typography fontSize={12} color="#7A747F" lineHeight={1.25}>
            {subtitle}
          </Typography>
          <Typography fontSize={18} fontWeight={600} color="#3A3440" lineHeight={1.15}>
            {title}
          </Typography>
        </Stack>
      </Stack>
    </MuiButton>
  );
}
